import { useTranslations } from '@aikoa/i18n/react'
import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'expo-router'
import { useState } from 'react'
import { View } from 'react-native'
import { formatIntervalToPostgres } from '~/components/admin/IntervalInput'
import {
  buildPromotionDataFromForm,
  getDefaultPromotionFormValues,
  isPromotionFormValid,
  PromotionForm,
  type PromotionFormValues,
} from '~/components/admin/PromotionForm'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { queryClient, trpc } from '~/lib/trpc'

export default function AddPromotion() {
  const { t } = useTranslations('admin')
  const router = useRouter()
  const [values, setValues] = useState<PromotionFormValues>(getDefaultPromotionFormValues())

  const createMutation = useMutation(
    trpc.admin.createPromotion.mutationOptions({
      onSuccess: (data) => {
        void queryClient.invalidateQueries({
          queryKey: ['admin', 'listPromotions'],
        })
        router.replace(`/(authenticated)/admin/promotions/${data.id}`)
      },
    })
  )

  return (
    <ScreenView>
      <ContentBox className="flex-1" contentContainerClassName="gap-6" variant="transparent">
        <View className="gap-4">
          <Heading2 className="text-center">{t(($) => $.promotions.create.title)}</Heading2>
          <Body className="text-center text-neutral-600">
            {t(($) => $.promotions.create.description)}
          </Body>
        </View>

        <PromotionForm values={values} onChange={setValues} />

        <View className="gap-4">
          <Button
            variant="primary"
            size="lg"
            disabled={!isPromotionFormValid(values) || createMutation.isPending}
            onPress={() =>
              createMutation.mutate({
                promotionCode: values.promotionCode.toUpperCase(),
                titleKey: values.titleKey,
                promotionAmount: Number.parseFloat(values.promotionAmount),
                promotionType: values.promotionType,
                validDuration:
                  values.promotionType === 'free_months'
                    ? null
                    : formatIntervalToPostgres(values.validDuration),
                maxActivations: values.maxActivations.trim()
                  ? Number.parseInt(values.maxActivations.trim(), 10)
                  : null,
                promotionData: buildPromotionDataFromForm(values.referralGeneration),
              })
            }>
            <ButtonLabel>
              {createMutation.isPending
                ? t(($) => $.promotions.create.buttonLoading)
                : t(($) => $.promotions.create.button)}
            </ButtonLabel>
          </Button>

          {!!createMutation.error && (
            <Body className="text-center text-destructive">
              {t(($) => $.promotions.create.error)}
            </Body>
          )}
        </View>
      </ContentBox>
    </ScreenView>
  )
}
